<script setup lang="ts">
import './index.scss';
import BurgerMenu from '~/components/Elements/BurgerMenu/index.vue';

const { status, signOut, getSession }: any = useAuth();
const props = defineProps({
    layout: {
        type: String as () => 'login' | 'dashboard' | 'owner',
        defaultValue: 'dashboard',
    },
})

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

const handleSignOut = async () => {
    try {

        const { data, status }: any = await useApi(`/api/user/logout`, {
            method: 'POST',
        })

        if(data && status == 'success') {
            await signOut({ callbackUrl: '/', redirect: true });
            await getSession();
        }

    } catch(error: any) {
        if (error.response) {
            const { statusCode, errorMessage, serverErrorMessage, serverErrorObject } = error.response._data?.error || {};

            console.error('Error', {
                statusCode: statusCode,
                errorMessage: errorMessage,
                serverErrorMessage: serverErrorMessage,
                serverErrorObject: serverErrorObject
            })

            showNotificationError(serverErrorMessage);

        } else {
            console.error("Unexpected error:", error);
        }
    }

}


const viewport = useViewport()

watch(() => viewport.breakpoint, (newBreakpoint, oldBreakpoint) => {
    console.log('Breakpoint updated:', oldBreakpoint, '->', newBreakpoint)
})

const linksOwner = [
    { href: "/restaurant-owner#benefit", text: "Registration Benefits" },
    { href: "/restaurant-owner#plans", text: "View Plans" },
    { href: "/restaurant-owner#faq", text: "FAQ" },
    { href: "/contact", text: "Contact Us" },
]


</script>

<template>
    <header v-if="props.layout == 'dashboard'"
        class="p-[1.55rem] fixed top-0 left-0 w-full bg-[white] max-md:py-[.7rem] max-md:px-[1.6rem] max-md:shadow-header-shadow z-[9999]">
        <div class="max-w-[112rem] mx-auto max-md:max-w-full">
            <div class="flex items-center gap-[3.2rem] justify-between max-md:gap-[2rem]">
                <h1 class="min-w-[11.5rem] max-md:min-w-[9.6rem]">
                    <NuxtLink to="/"
                        class="font-semibold leading-[1.5] text-[2.4rem] max-w-[14.5rem] max-md:max-w-[9.6rem] block">
                        <img class="w-full" src="/img/common/ico_logo.png" alt="logo">
                    </NuxtLink>
                </h1>
                <div class="flex gap-[3.2rem] max-md:gap-[.8rem]">
                    <ul class="flex items-center text-[primary] gap-[2.4rem] max-md:gap-[.8rem] max-md:flex-row-reverse"
                        v-if="status === 'authenticated'">
                        <NuxtLink to="/favorites"
                            class="font-normal text-[1.6rem] leading-[1.5] max-md:text-[1rem]">Favorites
                        </NuxtLink>
                        <NuxtLink to="/reservations"
                            class="font-normal text-[1.6rem] leading-[1.5] max-md:text-[1rem]">Reservations
                        </NuxtLink>

                    </ul>
                    <div class="header__authContainer text-[primary]">
                        <Block class="flex gap-[.8rem] flex-row flex-nowrap" v-if="status === 'authenticated'">
                            <ActionMenu>
                                <NuxtLink to="/profile"
                                    class="flex items-center justify-center px-[2rem] text-[1.4rem] leading-[1.5] font-bold hover:border-[darkGray] hover:bg-[bgSecond] transition ease-in-out delay-150 h-[3.7rem] hover:text-inherit">
                                    Profile
                                </NuxtLink>
                                <button @click="handleSignOut()"
                                    class=" w-full flex items-center justify-center px-[2rem] text-[1.4rem] leading-[1.5] font-bold hover:border-[darkGray] hover:bg-[bgSecond] transition ease-in-out delay-150 h-[3.7rem]">
                                    Sign Out
                                </button>
                            </ActionMenu>
                        </Block>
                        <Block class="flex gap-[.8rem] flex-row flex-nowrap" v-else>
                            <SignInModal ref="signInModalRef">
                                <template #accessButton="{ showModal }">
                                    <button type="button" @click="showModal"
                                        class="flex items-center justify-center px-[2rem] text-[1.4rem] leading-[1.5] font-bold rounded-[.8rem] border-solid border divide-[darkGray] hover:border-[darkGray] hover:bg-[bgSecond] transition ease-in-out delay-150 h-[3.7rem] max-md:py-[.8rem] max-md:px-[1.2rem] max-md:text-[1.2rem] max-md:border-[darkGray]">
                                        Sign In
                                    </button>
                                </template>
                            </SignInModal>
                            <RegisterModal ref="registerModalRef" />
                        </Block>

                    </div>
                </div>

            </div>

        </div>
    </header>

    <header class="relative z-[9999]" v-if="props.layout == 'login'">
        <div class="h-[7.2rem] max-w-[116rem] px-[2rem] mx-auto">
            <h1 class="text-[2.4rem] leading-[1.5] text-[white] font-semibold pt-[1.8rem]">
                <NuxtLink to="/" class="font-semibold leading-[1.5] text-[2.4rem] max-w-[20rem] block">
                    <img class="w-full" src="/img/common/ico_logo.png" alt="logo">
                </NuxtLink>
            </h1>
        </div>
    </header>

    <header v-if="props.layout == 'owner'" class="p-[1.55rem] fixed top-0 left-0 w-full z-[9999] bg-[white]">
        <div class="max-w-[112rem] mx-auto">
            <div class="flex items-center gap-[3.2rem] justify-between">
                <h1 class="min-w-[11.5rem]">
                    <NuxtLink to="/" class="font-semibold leading-[1.5] text-[2.4rem] max-w-[20rem] block max-md:max-w-[11.5rem]" @click="scrollToTop">
                        <img class="w-full" src="/img/common/ico_logo.png" alt="logo">
                    </NuxtLink>
                </h1>
                <div class="flex gap-[3.2rem]">
                    <component :is="viewport.isGreaterThan('sp') ? 'ul' : BurgerMenu" v-if="status === 'authenticated'"
                        :class="viewport.isGreaterThan('sp') ? 'flex items-center text-[primary] gap-[2.4rem]' : ''">
                        <NuxtLink v-for="link in linksOwner" :key="link.href" :to="link.href"
                            class="font-normal text-[1.6rem] leading-[1.5]" @click="scrollToTop">
                            {{ link.text }}
                        </NuxtLink>
                    </component>
                </div>

            </div>

        </div>
    </header>
</template>